import { useTranslation } from "react-i18next";
import { useDmReadings } from "@/api/districts/district-queries";
import type { Reading } from "@/api/reseller/types";
import { MeterTable } from "@/components/meters/meter-table";
import { useReadingsColumns } from "@/components/mqtt/readings-table-columns";
import { useTableUrlState } from "@/components/table/use-table-url-state";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useDistrict } from "@/contexts/district-context";
import { useDebounce } from "@/hooks/use-debounce";
import { useLocalizedNavigate } from "@/hooks/use-localized-navigate";

type StatusFilter = "all" | "completed" | "pending";

export function DmReadingsTable() {
	const { t } = useTranslation();
	const {
		page,
		setPage,
		pageSize,
		setPageSize,
		search,
		setSearch,
		ordering,
		setOrdering,
		filters,
		setFilter,
	} = useTableUrlState();
	const debouncedSearch = useDebounce(search);
	const navigate = useLocalizedNavigate();
	const { districtId } = useDistrict();

	const statusFilter = (filters.status ?? "all") as StatusFilter;

	const { data, isLoading } = useDmReadings({
		page,
		page_size: pageSize,
		search: debouncedSearch || undefined,
		ordering: ordering || undefined,
		completed:
			statusFilter === "all" ? undefined : statusFilter === "completed",
		district_id: districtId ?? undefined,
	});

	const columns = useReadingsColumns<Reading>({
		onDetailsClick: (row) => navigate("readings", String(row.id)),
	});

	const statusSelect = (
		<Select
			value={statusFilter}
			onValueChange={(v) => setFilter("status", v === "all" ? "" : v)}
		>
			<SelectTrigger className="h-9 w-40" aria-label="Filter by status">
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				<SelectItem value="all">{t("mqttReadingsTable.filterAll")}</SelectItem>
				<SelectItem value="completed">
					{t("mqttReadingsTable.filterCompleted")}
				</SelectItem>
				<SelectItem value="pending">
					{t("mqttReadingsTable.filterPending")}
				</SelectItem>
			</SelectContent>
		</Select>
	);

	return (
		<MeterTable
			data={data}
			columns={columns}
			loading={isLoading}
			page={page}
			pageSize={pageSize}
			onPageChange={setPage}
			onPageSizeChange={setPageSize}
			search={search}
			onSearchChange={setSearch}
			searchPlaceholder={t("mqttReadingsTable.searchGateway")}
			ordering={ordering}
			onOrderingChange={setOrdering}
			headerActions={<div className="flex gap-2">{statusSelect}</div>}
		/>
	);
}
